import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Plus, Minus, ShoppingBag, ArrowRight } from "lucide-react";
import { useCart } from "../context/CartContext";
import { CheckoutDialog } from "./CheckoutDialog";

export const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, total } = useCart();
  const { t } = useTranslation();
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  if (!open) return null;

  return (
    <>
      <div data-testid="cart-overlay" onClick={() => setOpen(false)}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60]" />
      <aside data-testid="cart-drawer"
        className="fixed top-0 right-0 bottom-0 w-full sm:w-[440px] bg-zinc-950 border-l-4 border-yellow-400 z-[70] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b-2 border-zinc-800">
          <div className="flex items-center gap-3">
            <ShoppingBag className="w-6 h-6 text-yellow-400" strokeWidth={3} />
            <div>
              <div className="font-heading text-2xl uppercase text-white leading-none">{t("cart.title")}</div>
              <div className="font-jp text-yellow-400 text-xs tracking-widest mt-1">買い物カゴ</div>
            </div>
          </div>
          <button data-testid="close-cart-button" onClick={() => setOpen(false)}
            className="p-2 text-white hover:bg-yellow-400 hover:text-black transition border-2 border-zinc-800">
            <X className="w-5 h-5" strokeWidth={3} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {items.length === 0 && (
            <div className="text-center py-20">
              <div className="font-heading text-3xl uppercase text-zinc-600 mb-2">{t("cart.empty")}</div>
              <div className="text-zinc-500 text-sm uppercase tracking-widest">{t("cart.empty_sub")}</div>
            </div>
          )}

          {items.map((it) => (
            <div key={it.product_id} data-testid={`cart-item-${it.product_id}`}
              className="flex gap-4 border-2 border-zinc-800 p-3 hover:border-yellow-400 transition">
              <div className="w-20 h-20 bg-zinc-900 border-2 border-zinc-800 flex-shrink-0 overflow-hidden">
                <img src={it.image} alt={it.title} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading uppercase text-white text-base leading-tight line-clamp-2">{it.title}</div>
                <div className="text-yellow-400 font-bold mt-1">€ {it.price.toFixed(2)}</div>
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center border-2 border-zinc-800">
                    <button data-testid={`qty-decrease-${it.product_id}`} onClick={() => updateQty(it.product_id, it.quantity - 1)}
                      className="p-1 text-white hover:bg-yellow-400 hover:text-black transition">
                      <Minus className="w-3 h-3" strokeWidth={3} />
                    </button>
                    <span className="px-3 font-heading text-white">{it.quantity}</span>
                    <button data-testid={`qty-increase-${it.product_id}`} onClick={() => updateQty(it.product_id, it.quantity + 1)}
                      className="p-1 text-white hover:bg-yellow-400 hover:text-black transition">
                      <Plus className="w-3 h-3" strokeWidth={3} />
                    </button>
                  </div>
                  <button data-testid={`remove-item-${it.product_id}`} onClick={() => removeItem(it.product_id)}
                    className="text-zinc-500 hover:text-red-500 text-xs uppercase tracking-widest">
                    {t("cart.remove")}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="border-t-2 border-zinc-800 p-6 space-y-4 bg-zinc-950">
          <div className="flex justify-between items-center">
            <span className="font-heading uppercase text-zinc-400 tracking-widest text-sm">{t("cart.subtotal")}</span>
            <span data-testid="cart-total" className="font-heading text-3xl text-yellow-400">€ {total.toFixed(2)}</span>
          </div>
          <button data-testid="checkout-button" disabled={items.length === 0} onClick={() => setCheckoutOpen(true)}
            className="w-full bg-yellow-400 text-black font-heading uppercase text-lg py-4 hover:bg-white transition flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
            {t("cart.checkout")} <ArrowRight className="w-5 h-5" strokeWidth={3} />
          </button>
          <div className="text-center text-zinc-500 text-xs uppercase tracking-widest">{t("cart.free_shipping")}</div>
        </div>
      </aside>

      <CheckoutDialog open={checkoutOpen} onClose={() => setCheckoutOpen(false)} />
    </>
  );
};
